import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit, Trash2, X, Calendar as CalendarIcon, Filter } from 'lucide-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchShops } from '../services/shopService';
import {
  fetchExpenseTypes,
  fetchExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
} from '../services/expenseService';

const initialForm = {
  shopId: '',
  expenseTypeId: '',
  title: '',
  amount: '',
  expenseDate: '',
  notes: '',
  attachmentUrl: '',
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '₹0.00';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(num);
};

export default function Expenses() {
  const [shops, setShops] = useState([]);
  const [types, setTypes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchShops({ limit: 200 })
      .then((res) => {
        const records = res.data?.data?.records || res.data?.data || [];
        setShops(records);
      })
      .catch(() => toast.error('Failed to load shops'));

    fetchExpenseTypes({ limit: 200, includeInactive: false })
      .then((res) => {
        const records = res.data?.data?.records || [];
        setTypes(records.filter((type) => type.isActive !== false));
      })
      .catch(() => toast.error('Failed to load expense types'));
  }, []);

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, shopFilter, typeFilter, startDate, endDate]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const res = await fetchExpenses({
        page,
        limit,
        search,
        shopId: shopFilter || undefined,
        expenseTypeId: typeFilter || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const records = res.data?.data?.records || [];
      const meta = res.data?.data?.meta || {};
      setExpenses(records);
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || records.length);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const openDrawer = (expense = null) => {
    if (expense) {
      setEditingId(expense.id);
      setForm({
        shopId: expense.shopId ? String(expense.shopId) : String(expense.shop?.id || ''),
        expenseTypeId: expense.expenseTypeId
          ? String(expense.expenseTypeId)
          : String(expense.expenseType?.id || ''),
        title: expense.title || '',
        amount: expense.amount != null ? String(expense.amount) : '',
        expenseDate: expense.expenseDate ? expense.expenseDate.slice(0, 10) : '',
        notes: expense.notes || '',
        attachmentUrl: expense.attachmentUrl || '',
      });
    } else {
      setEditingId(null);
      setForm(initialForm);
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.shopId) {
      toast.error('Select a shop');
      return;
    }
    if (!form.expenseTypeId) {
      toast.error('Select an expense type');
      return;
    }
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.amount || Number.isNaN(Number(form.amount))) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!form.expenseDate) {
      toast.error('Pick an expense date');
      return;
    }

    const payload = {
      shopId: Number(form.shopId),
      expenseTypeId: Number(form.expenseTypeId),
      title: form.title.trim(),
      amount: Number(form.amount),
      expenseDate: form.expenseDate,
      notes: form.notes,
      attachmentUrl: form.attachmentUrl,
    };

    try {
      if (editingId) {
        await updateExpense(editingId, payload);
        toast.success('Expense updated');
      } else {
        await createExpense(payload);
        toast.success('Expense created');
      }
      closeDrawer();
      loadExpenses();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save expense');
    }
  };

  const handleDelete = async (expense) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete expense?',
      text: `Are you sure you want to delete ${expense.title}?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteExpense(expense.id);
      toast.success('Expense deleted');
      loadExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const totalStart = (page - 1) * limit + 1;
  const totalEnd = Math.min(totalStart + limit - 1, totalRecords);

  const typeMap = useMemo(() => {
    const map = new Map();
    types.forEach((type) => map.set(String(type.id), type.name));
    return map;
  }, [types]);

  const shopMap = useMemo(() => {
    const map = new Map();
    shops.forEach((shop) => map.set(String(shop.id), shop.name));
    return map;
  }, [shops]);

  return (
    <LayoutWithSidebar>
      <div className="p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-gray-800">Expenses</h1>
            <p className="text-sm text-gray-500">Track spending across shops and categories.</p>
          </div>
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-gray-500" />
              <select
                value={shopFilter}
                onChange={(e) => {
                  setPage(1);
                  setShopFilter(e.target.value);
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Shops</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.name}
                  </option>
                ))}
              </select>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setPage(1);
                  setTypeFilter(e.target.value);
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">All Types</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <CalendarIcon size={16} className="absolute left-2 top-2.5 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setPage(1);
                    setStartDate(e.target.value);
                  }}
                  className="pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <span className="text-xs text-gray-500">to</span>
              <div className="relative">
                <CalendarIcon size={16} className="absolute left-2 top-2.5 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setPage(1);
                    setEndDate(e.target.value);
                  }}
                  className="pl-8 pr-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search title or notes"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => openDrawer()}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm shadow hover:bg-indigo-700"
            >
              <Plus size={16} />
              Add Expense
            </button>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Title</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Shop</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Notes</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                      Loading expenses...
                    </td>
                  </tr>
                ) : expenses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                      No expenses found.
                    </td>
                  </tr>
                ) : (
                  expenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{expense.title}</div>
                        {expense.attachmentUrl && (
                          <a
                            href={expense.attachmentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-indigo-600 hover:underline"
                          >
                            View attachment
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {expense.expenseType?.name || typeMap.get(String(expense.expenseTypeId)) || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {expense.shop?.name || shopMap.get(String(expense.shopId)) || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">{formatCurrency(expense.amount)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {expense.expenseDate ? new Date(expense.expenseDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate" title={expense.notes || ''}>
                        {expense.notes || '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openDrawer(expense)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 rounded-full hover:bg-indigo-50"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(expense)}
                            className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-600">
            <div>
              Showing {totalRecords === 0 ? 0 : totalStart}-{totalEnd} of {totalRecords}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className={`px-3 py-1 border rounded-md ${page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={page >= totalPages}
                className={`px-3 py-1 border rounded-md ${page >= totalPages ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
              >
                Next
              </button>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="border border-gray-300 rounded-md px-2 py-1"
              >
                {[10, 20, 50].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 bg-black/30 z-50 flex justify-end">
          <div className="w-full max-w-xl bg-white h-full shadow-xl flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {editingId ? 'Edit Expense' : 'Create Expense'}
                </h2>
                <p className="text-xs text-gray-500">
                  Record expenditure details to keep finances up to date.
                </p>
              </div>
              <button type="button" onClick={closeDrawer} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Shop</label>
                  <select
                    value={form.shopId}
                    onChange={(e) => setForm((prev) => ({ ...prev, shopId: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select shop</option>
                    {shops.map((shop) => (
                      <option key={shop.id} value={shop.id}>
                        {shop.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expense Type</label>
                  <select
                    value={form.expenseTypeId}
                    onChange={(e) => setForm((prev) => ({ ...prev, expenseTypeId: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  >
                    <option value="">Select type</option>
                    {types.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expense Date</label>
                  <input
                    type="date"
                    value={form.expenseDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, expenseDate: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={4}
                  placeholder="Optional details about this expense"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Attachment URL</label>
                <input
                  type="url"
                  value={form.attachmentUrl}
                  onChange={(e) => setForm((prev) => ({ ...prev, attachmentUrl: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="https://"
                />
              </div>
            </form>
            <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50">
              <button
                type="button"
                onClick={closeDrawer}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md shadow hover:bg-indigo-700"
              >
                {editingId ? 'Update Expense' : 'Create Expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
